import gql from 'graphql-tag';
import className from 'classnames/bind';
import { Container, RevealWrapper, SectionHeader, RhLink } from '@components';
import { ListFragment } from '@graphqlTypes';
import { getLinkData } from '@/core/utils';
import { useRef } from 'react';
import { useInView } from 'motion/react';
import { useViewSize } from '@/providers/ViewSizeContext';
import { AnimVals } from '@/utils/animUtils';

import styles from './List.module.scss';
const cx = className.bind(styles);

type TextItem = ListFragment['textItems'][number];
type LinkItem = ListFragment['linkItems'][number];

const TextItem = ({ item, index, count }: { item: TextItem, index: number, count: number }) => {
  const { headline, body, cta } = item;
  const ctaData = getLinkData(cta);
  return (
    <li className={
      cx([
        "list-item",
        "list-item--text",
        { 'list-item--last-row': index > count - 3 } // last two items
    ])}>
      <h3 className={cx("title", "title-lc-s")}>{headline}</h3>
      <div className={cx("content")}>
        <div className={cx("body", "text-m")} dangerouslySetInnerHTML={{ __html: body }} />
        {ctaData && (
          <RhLink className={cx("link", "inline-link", "content--link")} href={ctaData.url} target={ctaData.target}>
            {ctaData.label}
          </RhLink>
        )}
      </div>
    </li>
  )
};

const LinkItem = ({ item, index, count }: { item: LinkItem, index: number; count: number }) => {
  const { headline, cta } = item;
  const ctaData = getLinkData(cta);
  return (
    <li
      className={cx([
        "list-item",
        "list-item--link",
        { 'list-item--last-row': index > count - 3 } // last two items
      ])}>
      <h3 className={cx("title", "title-lc-s")}>{headline}</h3>
      {ctaData && (
        <RhLink className={cx("link", "inline-link", "list-item--link--link")} href={ctaData.url} target={ctaData.target}>
          {ctaData.label}
        </RhLink>
      )}
    </li>
  );
}

export default function List(props: ListFragment) {
  const { headline, subhead, type, textItems, linkItems } = props;
  const isTextList = type[0] === 'text-list';
  const items = isTextList ? textItems : linkItems;
  const showHeader = Boolean(headline || subhead);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true; // isDesktop;

  // -------------------- Render

  return (
    <div className={cx(["list"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          { showHeader && (
            <SectionHeader
              headline={headline}
              subhead={subhead}
              isRevealEnabled={isRevealEnabled}
              isInView={isInView}
            />
          )}
          <ul className={cx("list__items")}>
            {items.map((item, index) => (
              isTextList ? (
                <TextItem key={index} item={item as TextItem} index={index} count={items.length} />
              ) : (
                <LinkItem key={index} item={item as LinkItem} index={index} count={items.length} />
              )
            ))}
          </ul>
        </Container>
      </RevealWrapper>
    </div>
  );

}

List.fragments = {
  entry: gql`
    fragment List on FlexiblePageContentContentListLayout {
      headline
      subhead
      type
      textItems {
        headline
        body
        cta {
          linkType
          externalLink
          label
          page {
            node {
              uri
            }
          }
        }
      }
      linkItems {
        headline
        cta {
          linkType
          externalLink
          label
          page {
            node {
              uri
            }
          }
        }
      }
    }
  `,
};
